import React, { useState, useEffect } from 'react';
import { 
  Milk, Database, Home, MapPin, Scale, 
  GraduationCap, ShieldAlert, Briefcase, Zap 
} from 'lucide-react';

const MyApps = () => {
  const [portals, setPortals] = useState([]);
  const [loadingApp, setLoadingApp] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('portals');
    setPortals(stored ? JSON.parse(stored) : []);
  }, []);

  // Keyed by exact portal_name from DB — add new portals here as needed
  const portalConfig = {
    'Amudhasurabhi':                          { id: 'amudhasurabhi', icon: Milk,          bgColor: 'bg-blue-50',   iconColor: 'text-blue-600',   borderColor: 'border-blue-200',   url: null,                                    ssoCallback: null },
    'Development Action Plan (DAP) Portal':   { id: 'dap',           icon: Database,      bgColor: 'bg-purple-50', iconColor: 'text-purple-600', borderColor: 'border-purple-200', url: null,                                    ssoCallback: null },
    'Hostel Management Information System (HMIS)': { id: 'hmis',     icon: Home,          bgColor: 'bg-green-50',  iconColor: 'text-green-600',  borderColor: 'border-green-200',  url: null,                                    ssoCallback: null },
    'Land Acquisition':                       { id: 'land',          icon: MapPin,        bgColor: 'bg-yellow-50', iconColor: 'text-yellow-600', borderColor: 'border-yellow-200', url: null,                                    ssoCallback: 'https://laop-adtw.in/sso/callback' },
    'Prevention of Atrocities (POA)':         { id: 'poa',           icon: ShieldAlert,   bgColor: 'bg-red-50',    iconColor: 'text-red-600',    borderColor: 'border-red-200',    url: 'https://tnadwpoa.tn.gov.in/#/landing1', ssoCallback: null },
    'Legal Management System (LMS)':          { id: 'lms',           icon: Scale,         bgColor: 'bg-pink-50',   iconColor: 'text-pink-600',   borderColor: 'border-pink-200',   url: 'https://adwlms.com/sso/login',          ssoCallback: null },
    'Overseas Scholarship Portal':            { id: 'overseas',      icon: GraduationCap, bgColor: 'bg-indigo-50', iconColor: 'text-indigo-600', borderColor: 'border-indigo-200', url: null,                                    ssoCallback: null },
    'Project Management Office (PMO)':        { id: 'pmo',           icon: Briefcase,     bgColor: 'bg-teal-50',   iconColor: 'text-teal-600',   borderColor: 'border-teal-200',   url: null,                                    ssoCallback: null },
    'Tamil Nadu State Scholarship Portal (TNSSP)': { id: 'tnssp',    icon: GraduationCap, bgColor: 'bg-orange-50', iconColor: 'text-orange-600', borderColor: 'border-orange-200', url: null,                                    ssoCallback: null },
    'Viduthipaarvai':                         { id: 'viduthipaarvai',icon: Zap,           bgColor: 'bg-cyan-50',   iconColor: 'text-cyan-600',   borderColor: 'border-cyan-200',   url: null,                                    ssoCallback: null },
  };

  const apps = portals.map(p => ({
    ...portalConfig[p.portal_name] || { id: p.portal_name, icon: Scale, bgColor: 'bg-gray-50', iconColor: 'text-gray-600', borderColor: 'border-gray-200', url: null, ssoCallback: null },
    name: p.portal_name,
  }));

  const handleAppClick = (app) => {
    if (app.ssoCallback) {
      setLoadingApp(app.id);
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const parsedUser = userStr ? JSON.parse(userStr) : {};
      const email = parsedUser.email || '';
      setTimeout(() => {
        window.location.href = `${app.ssoCallback}?token=${token}&username=${email}`;
      }, 1000);
      return;
    }
    if (!app.url) return;
    setLoadingApp(app.id);
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const parsedUser = userStr ? JSON.parse(userStr) : {};
    const email = parsedUser.email || '';
    setTimeout(() => {
      window.open(`${app.url}?token=${token}&username=${email}`, '_blank');
      setLoadingApp(null);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {loadingApp && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-[#3d82c4] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-700 font-medium text-sm">Redirecting to portal...</p>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-800">My Apps</h1>
        <p className="text-sm text-gray-600 mt-1">Access your portal applications</p>
      </div>

      {apps.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => {
            const Icon = app.icon;
            const isLoading = loadingApp === app.id;
            const isClickable = app.url || app.ssoCallback;
            return (
              <div
                key={app.id}
                onClick={() => handleAppClick(app)}
                className={`${app.bgColor} border ${app.borderColor} p-6 hover:shadow-lg transition-all group relative ${isClickable ? 'cursor-pointer' : 'cursor-default'} ${isLoading ? 'opacity-70' : ''}`}
              >
                <div className={`w-12 h-12 ${app.bgColor} border ${app.borderColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-6 h-6 ${app.iconColor}`} />
                </div>
                <h3 className="text-base font-semibold text-gray-800 mb-2">{app.name}</h3>
                <p className="text-xs text-gray-600">
                  {isClickable ? 'Click to open portal' : 'Click to access portal'}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">No apps available for your account.</p>
        </div>
      )}
    </div>
  );
};

export default MyApps;